import * as vp from 'vopay';

import * as Yup from 'yup';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Formik, FormikErrors, FormikHandlers, FormikHelpers, FormikTouched, useFormikContext } from 'formik';

import styles from '../AccountingSystemOpen.module.scss';
import { PaymentUpdate, PaymentInformation } from '../types';
import BankingUpdateMiniDialogue from '../BankingUpdateMiniDialogue/BankingUpdateMiniDialogue';
import { useBuildPaymentInfoRows, PaymentInfoRows } from './buildPaymentInfoRows.helper';
import useFetchAspInvoiceCreditNotes from '#shared/components/AccountingSoftware/hooks/useFetchAspInvoiceCreditNotes';
import useFetchAspBillCreditNotes from '#shared/components/AccountingSoftware/hooks/useFetchAspBillCreditNotes';
import { getAspRowExtras } from '#shared/components/AccountingSoftware/aspAccountingAdapter';
import usePersistAspPaymentMethod from '#shared/components/AccountingSoftware/usePersistAspPaymentMethod';
import { useAccountingSoftware } from '#shared/components/AccountingSoftware/AccountingSoftwareContext';
import {
  getDefaultBankingPaymentRails,
  getDefaultPaymentSelectionMethods,
  toElinxCountry,
} from '#shared/components/AccountingSoftware/elinxDefaults.helper';

import {
  updateCustomerData,
  getAvailablePaymentMethodsDropdown,
  getRequiredIcon,
  getAvailablePaymentNew,
  roundCurrency,
  seedElinxDefaults,
  mergeElinxDefaults,
  isFutureScheduled,
} from '../utils';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useTranslation } from 'react-i18next';

import { Button } from '#shared/components/UI';

type AccountingSystemSingleDialogueProps = {
  isVisible: boolean;
  billsAndInvoices?: vp.TemporaryBillsOrInvoicesRails;
  financialInstitutions: {
    [key: string]: vp.FinancialInstitution;
  };
  onCollect: (savedValues: vp.TemporaryBillsOrInvoicesRails, isUpdated: boolean) => void;
  close: () => void;
  type: 'Payable' | 'Receivable';
};

type FC = FunctionComponent<AccountingSystemSingleDialogueProps>;

const { Modal, SubTitle, Box, Select, DatepickerSimple, ElementContainer, TextField, MultiSelectChip, Checkbox, Icon } =
  vp;

type SingleDialogueFormEffectsProps = {
  setCurrentValues: (values: PaymentInformation) => void;
  paymentInfoRows: PaymentInfoRows[];
  totalAmount: number;
  isVisible: boolean;
  billsAndInvoices?: vp.TemporaryBillsOrInvoicesRails;
  billsAndInvoicesInfo?: vp.TemporaryBillsOrInvoicesRails;
  paymentMethods: ReturnType<typeof getAvailablePaymentMethodsDropdown>;
  elinxSeed: Record<string, unknown>;
};

function SingleDialogueFormEffects({
  setCurrentValues,
  paymentInfoRows,
  totalAmount,
  isVisible,
  billsAndInvoices,
  billsAndInvoicesInfo,
  paymentMethods,
  elinxSeed,
}: SingleDialogueFormEffectsProps) {
  const { values, errors, setFieldValue, validateForm } = useFormikContext<PaymentInformation>();

  useEffect(() => {
    setCurrentValues({
      ...values,
    });
  }, [values]);

  useEffect(() => {
    validateForm();
  }, [values, errors, paymentInfoRows]);

  useEffect(() => {
    setFieldValue('PaymentAmount', String(roundCurrency(totalAmount - values.CreditAmount)) || '');

    if (isFutureScheduled(values.PaymentDate) && values.PaymentMethod === 'eLinx') {
      setFieldValue('PaymentMethod', '');
    }
  }, [values.PaymentDate, values.CreditNotes, isVisible]);

  useEffect(() => {
    if (isVisible) {
      setFieldValue('PaymentMethod', billsAndInvoices?.paymentMethodUser || paymentMethods[0].value || '');
      Object.entries(elinxSeed).forEach(([key, value]) => setFieldValue(key, value));
    } else {
      setFieldValue('CreditNotes', '');
      setFieldValue('CreditAmount', 0);
    }
  }, [isVisible]);

  useEffect(() => {
    if (billsAndInvoicesInfo?.paymentMethodUser !== values.PaymentMethod) {
      setFieldValue('PaymentMethod', billsAndInvoicesInfo?.paymentMethodUser || paymentMethods[0].value || '');
    }
  }, [billsAndInvoicesInfo?.paymentMethodUser]);

  return null;
}

const AccountingSystemSingleDialogue: FC = ({
  onCollect,
  isVisible,
  close,
  type,
  billsAndInvoices,
  financialInstitutions,
}: AccountingSystemSingleDialogueProps) => {
  const [updateBankVisible, setUpdateBankVisible] = useState(false);
  const [creditNotes, setCreditNotes] = useState<vp.CreditNote[]>([]);
  const { fetchAspInvoiceCreditNotes } = useFetchAspInvoiceCreditNotes();
  const { fetchAspBillCreditNotes } = useFetchAspBillCreditNotes();

  const [billsAndInvoicesInfo, setBillsAndInvoicesInfo] = useState<vp.TemporaryBillsOrInvoicesRails | undefined>(
    billsAndInvoices,
  );

  const blockingLoader = useBlockingLoader();
  const [isUpdated, setIsUpdated] = useState(false);
  const { persistPaymentMethod } = usePersistAspPaymentMethod();
  const { account: response } = useAccountingSoftware();
  const { Country } = response;
  const { LockedSelectOptions } = useAccountingSoftware().account;
  const elinxFormType: 'Pay' | 'Collect' = type === 'Receivable' ? 'Collect' : 'Pay';

  const elinxSeed = useMemo(
    () => ({
      ...seedElinxDefaults(billsAndInvoices?.Customer),
      ElinxPaymentSelectionMethod: getDefaultPaymentSelectionMethods(
        elinxFormType,
        toElinxCountry(Country),
        LockedSelectOptions,
        undefined,
        'single',
      ),
      ElinxBankingPaymentRail: getDefaultBankingPaymentRails(
        elinxFormType,
        toElinxCountry(Country),
        LockedSelectOptions,
        undefined,
        'single',
      ),
    }),
    [billsAndInvoices, Country, LockedSelectOptions, elinxFormType],
  );

  const [defaultValues, setDefaultValues] = useState<PaymentUpdate>({
    PaymentMethod: billsAndInvoices?.paymentMethodUser || 'Bank Account',
    FinancialInstitutionNumber: billsAndInvoices?.Customer?.BankAccount?.FinancialInstitutionNumber,
    BankTransitNumber: billsAndInvoices?.Customer?.BankAccount?.BranchTransitNumber,
    AccountNumber: billsAndInvoices?.Customer?.BankAccount?.AccountNumber,
    Email: billsAndInvoices?.Customer?.Email || billsAndInvoices?.Customer?.Interac?.EmailAddress,
    Phone: billsAndInvoices?.Customer?.Phone || billsAndInvoices?.Customer?.Interac?.PhoneNumber || '',
    Question: billsAndInvoices?.Customer?.Interac?.Question,
    Answer: billsAndInvoices?.Customer?.Interac?.Answer,
    PhoneNumber: billsAndInvoices?.Customer?.BankAccount?.PhoneNumber,
    City: billsAndInvoices?.Customer?.BankAccount?.City,
    Address: billsAndInvoices?.Customer?.BankAccount?.Address,
    State: billsAndInvoices?.Customer?.BankAccount?.State,
    ZipCode: billsAndInvoices?.Customer?.BankAccount?.ZipCode,
    InteracMethod: billsAndInvoices?.Customer?.Interac?.EmailAddress
      ? 'Email Address'
      : billsAndInvoices?.Customer?.Interac?.PhoneNumber
        ? 'Phone Number'
        : 'Email Address',
    TokenDebit: billsAndInvoices?.Customer?.DebitCard?.Token,
    TokenCredit: billsAndInvoices?.Customer?.CreditCard?.Token,
    Language: billsAndInvoices?.Customer?.Interac?.Language || 'en',
  });

  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.OpenTab.Dialogs.SingleDialog' });
  const { t: tRoot } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      if (isVisible) {
        blockingLoader.show();

        const extras = billsAndInvoices ? getAspRowExtras(billsAndInvoices) : null;

        if (extras && extras.AspContactID !== null) {
          const request = { ConnectionID: extras.AspConnectionID, ContactID: extras.AspContactID };

          try {
            const creditsResponse =
              type === 'Receivable'
                ? await fetchAspInvoiceCreditNotes(request)
                : await fetchAspBillCreditNotes(request);

            setCreditNotes(creditsResponse.CreditNotes);
          } catch {
            // Credit notes are optional context for the dialog — same
            // fallback as the legacy non-Success branch.
            setCreditNotes([]);
          }
        }

        blockingLoader.hide();
      } else {
        setCreditNotes([]);
      }
    };

    fetchData();
  }, [isVisible]);

  useEffect(() => {
    setBillsAndInvoicesInfo(billsAndInvoices);
  }, [billsAndInvoices]);

  useEffect(() => {
    setDefaultValues({
      PaymentMethod: billsAndInvoicesInfo?.paymentMethodUser || 'Bank Account',
      FinancialInstitutionNumber: billsAndInvoicesInfo?.Customer?.BankAccount?.FinancialInstitutionNumber,
      BankTransitNumber: billsAndInvoicesInfo?.Customer?.BankAccount?.BranchTransitNumber,
      AccountNumber: billsAndInvoicesInfo?.Customer?.BankAccount?.AccountNumber,
      Email: billsAndInvoicesInfo?.Customer?.Email || billsAndInvoicesInfo?.Customer?.Interac?.EmailAddress,
      Phone: billsAndInvoicesInfo?.Customer?.Phone || billsAndInvoicesInfo?.Customer?.Interac?.PhoneNumber || '',
      Question: billsAndInvoicesInfo?.Customer?.Interac?.Question,
      Answer: billsAndInvoicesInfo?.Customer?.Interac?.Answer,
      PhoneNumber: billsAndInvoicesInfo?.Customer?.BankAccount?.PhoneNumber,
      City: billsAndInvoicesInfo?.Customer?.BankAccount?.City,
      Address: billsAndInvoicesInfo?.Customer?.BankAccount?.Address,
      State: billsAndInvoicesInfo?.Customer?.BankAccount?.State,
      ZipCode: billsAndInvoicesInfo?.Customer?.BankAccount?.ZipCode,
      InteracMethod: billsAndInvoicesInfo?.Customer?.Interac?.EmailAddress
        ? 'Email Address'
        : billsAndInvoicesInfo?.Customer?.Interac?.PhoneNumber
          ? 'Phone Number'
          : 'Email Address',
      TokenDebit: billsAndInvoicesInfo?.Customer?.DebitCard?.Token,
      TokenCredit: billsAndInvoicesInfo?.Customer?.CreditCard?.Token,
      Language: billsAndInvoicesInfo?.Customer?.Interac?.Language || 'en',
    });
  }, [billsAndInvoicesInfo]);

  const [updateType, setUpdateType] = useState<'Add' | 'Edit'>('Edit');

  const onEdit = (paymentMethod: string) => {
    setUpdateType('Edit');

    setDefaultValues({
      ...defaultValues,
      PaymentMethod: paymentMethod,
      PaymentDate: currentValues.PaymentDate,
      ...mergeElinxDefaults(currentValues, billsAndInvoicesInfo?.Customer),
    });

    setUpdateBankVisible(true);
  };

  const onAddNew = () => {
    setUpdateType('Add');

    setDefaultValues({
      ...defaultValues,
      PaymentMethod: '',
      PaymentDate: currentValues.PaymentDate,
      ...mergeElinxDefaults(currentValues, billsAndInvoicesInfo?.Customer),
    });

    setUpdateBankVisible(true);
  };

  const paymentMethods = getAvailablePaymentMethodsDropdown(billsAndInvoicesInfo, Country, type, onEdit, tRoot);

  const totalAmount =
    Number(vp.sanitizeToNumeric(String(billsAndInvoices?.amountDue))) ||
    Number(vp.sanitizeToNumeric(String(billsAndInvoices?.totalAmount))) ||
    0;

  const paymentInfoRows = useBuildPaymentInfoRows({
    type,
    maximumDate: billsAndInvoices?.dueDate || '',
    currency: billsAndInvoices?.currency || '',
    paymentMethods,
    paymentMethodsHeader: ({ close: closeDropdown }) =>
      getAvailablePaymentNew(
        billsAndInvoicesInfo,
        type,
        () => {
          onAddNew();
          closeDropdown();
        },
        tRoot,
      ),
    totalAmount,
    creditNotes,
  });

  const [currentValues, setCurrentValues] = useState<PaymentInformation>({
    PaymentDate: '',
    PaymentAmount: '',
    PaymentMethod: '',
    CreditNotes: '',
    CreditAmount: 0,
  });

  const onSave = (values: PaymentInformation) => {
    if (billsAndInvoicesInfo) {
      onCollect(
        updateCustomerData(
          {
            ...defaultValues,
            PaymentMethod: values.PaymentMethod,
            IsManualEntry: values.PaymentMethod !== 'eLinx',
          },
          billsAndInvoicesInfo,
          Country,
          type,
          values.PaymentAmount || '',
          new Date(Number(values.PaymentDate)).toLocaleDateString('en-ca'),
          values.CreditNotes,
        ),
        isUpdated,
      );
    }
  };

  const paymentInformation = useMemo(
    () => ({
      PaymentDate: Date.now().toString(),
      PaymentAmount: billsAndInvoices?.PaidAmount || '0.00',
      PaymentMethod: billsAndInvoices?.paymentMethodUser || paymentMethods[0].value || '',
      CreditNotes: '',
      CreditAmount: 0,
      ...elinxSeed,
    }),
    [billsAndInvoices, paymentMethods, elinxSeed],
  );

  return (
    <Modal isVisible={isVisible} enableBgClick={false}>
      <div className={[styles.dialogue, updateBankVisible ? styles.disabledOverview : ''].join(' ')}>
        <BankingUpdateMiniDialogue
          isVisible={updateBankVisible}
          type={type}
          updateType={updateType}
          financialInstitutions={financialInstitutions}
          close={() => setUpdateBankVisible(false)}
          onSave={async (values: PaymentUpdate) => {
            // On asp rows the method must live on the contact's client account
            // before collecting — a failed save keeps the dialog open.
            if (billsAndInvoicesInfo && !(await persistPaymentMethod(billsAndInvoicesInfo, values))) return;

            setDefaultValues(values);
            setUpdateBankVisible(false);
            setIsUpdated(true);

            if (billsAndInvoicesInfo) {
              const newValues = updateCustomerData(
                values,
                billsAndInvoicesInfo,
                Country,
                type,
                billsAndInvoicesInfo.PaidAmount || '',
                billsAndInvoicesInfo.scheduledDate || '',
              );

              setBillsAndInvoicesInfo(newValues);
            }
          }}
          originalValue={billsAndInvoicesInfo}
          defaultValues={defaultValues}
        />

        <div className={styles.dialogueContent}>
          <Formik<PaymentInformation>
            initialValues={paymentInformation}
            onSubmit={(values, { resetForm }) => {
              onSave(values);
              resetForm();
            }}
            validationSchema={Yup.object().shape(getValidationSchema(currentValues, paymentInfoRows))}
            validateOnChange
            validateOnBlur
          >
            {({
              isValid,
              handleSubmit,
              resetForm,
              submitForm,
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setValues,
              setTouched,
              setFieldValue,
              validateForm,
            }) => {
              return (
                <form onSubmit={handleSubmit} className={styles.dialogueForm}>
                  <SingleDialogueFormEffects
                    setCurrentValues={setCurrentValues}
                    paymentInfoRows={paymentInfoRows}
                    totalAmount={totalAmount}
                    isVisible={isVisible}
                    billsAndInvoices={billsAndInvoices}
                    billsAndInvoicesInfo={billsAndInvoicesInfo}
                    paymentMethods={paymentMethods}
                    elinxSeed={elinxSeed}
                  />

                  <Icon
                    className={styles.closeButton}
                    icon="cross"
                    height="12px"
                    width="12px"
                    onClick={() => {
                      resetForm();
                      validateForm();
                      close();
                    }}
                  />

                  <Box base="8" gap="lg" flx="col">
                    <div>
                      <SubTitle
                        subTitle={
                          type === 'Receivable'
                            ? t('collectTitle', { defaultValue: 'Collect Receivable' })
                            : t('sendTitle', { defaultValue: 'Send Payment' })
                        }
                        center
                      />
                    </div>

                    <Box width="100%" gapSize="24px" base="8" flx="col">
                      {paymentInfoRows
                        .filter((element) => element.visible(values))
                        .map((element) =>
                          getFormElement(
                            element,
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            setValues,
                            setTouched,
                            setFieldValue,
                          ),
                        )}
                    </Box>

                    <Box gap="sm" flx="row">
                      <Button
                        id="create-collection"
                        colorScheme="primary"
                        onClick={submitForm}
                        disabled={!isValid}
                        style={{ width: '100%' }}
                      >
                        {type === 'Receivable'
                          ? t('collectButton', { defaultValue: 'Collect' })
                          : t('payButton', { defaultValue: 'Pay' })}
                      </Button>
                    </Box>
                  </Box>
                </form>
              );
            }}
          </Formik>
        </div>
      </div>
    </Modal>
  );

  function getFormElement(
    element: PaymentInfoRows,
    values: PaymentInformation,
    errors: FormikErrors<PaymentInformation>,
    touched: FormikTouched<PaymentInformation>,
    handleChange: FormikHandlers['handleChange'],
    handleBlur: FormikHandlers['handleBlur'],
    setValues: (values: React.SetStateAction<PaymentInformation>, shouldValidate?: boolean | undefined) => void,
    setTouched: (touched: FormikTouched<PaymentInformation>, shouldValidate?: boolean | undefined) => void,
    setFieldValue: FormikHelpers<PaymentInformation>['setFieldValue'],
  ) {
    const {
      type: elementType,
      id,
      preset,
      placeholder,
      label,
      subtitle,
      require: required,
      getSelectOptions,
      getMultiSelectOptions,
      container,
      paymentRows,
      min,
      getElementLeft,
      getElementRight,
      disabled,
      renderOption,
      renderSelectedValue,
      optionVisibility,
      renderDropdownHeader,
      disabledMessage,
    } = element;

    if (elementType === 'elinx') {
      return element.render
        ? element.render({ values, errors, touched, handleChange, handleBlur, setFieldValue })
        : null;
    }

    if (container === 'inputs') {
      const isRequired = required(values);

      return (
        <Box flx="col" width="100%">
          <ElementContainer
            width="100%"
            padding="0 0 0"
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            isBold={false}
            input={
              <Box flx="col" width="100%">
                {paymentRows
                  .filter((subElement) => subElement.visible(values))
                  .map((subElement) =>
                    getFormElement(
                      subElement,
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      setValues,
                      setTouched,
                      setFieldValue,
                    ),
                  )}
              </Box>
            }
          />
        </Box>
      );
    }

    if (container === 'wrapper' || id === undefined) {
      return (
        <Box gap="sm" ai="fs" width="100%">
          {paymentRows
            .filter((subElement) => subElement.visible(values))
            .map((subElement) =>
              getFormElement(
                subElement,
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setValues,
                setTouched,
                setFieldValue,
              ),
            )}
        </Box>
      );
    }

    const isRequired = required(values);

    switch (elementType) {
      case 'input':
        return (
          <ElementContainer
            width="100%"
            padding="0 0 6px"
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            isBold={false}
            input={
              <Box flx="col" gapSize="6px">
                <TextField
                  variant="outlineShadow"
                  preset={preset}
                  name={id}
                  placeholder={placeholder}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  inputWidth="calc(100% - 172px)"
                  value={String(values[id] || '')}
                  disabled={disabled ? disabled(values) : false}
                  elementLeft={getElementLeft ? getElementLeft(values) : undefined}
                  elementRight={getElementRight ? getElementRight(values) : undefined}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />

                {disabled && disabled(values) && disabledMessage && (
                  <div className={styles.disabledMessage}>{disabledMessage}</div>
                )}
              </Box>
            }
          />
        );
      case 'select':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            padding="0 0 6px"
            input={
              <Select
                version="2"
                options={
                  getSelectOptions
                    ? getSelectOptions(values).filter((option) => (optionVisibility ? optionVisibility(option) : true))
                    : []
                }
                renderOption={renderOption}
                renderSelectedValue={renderSelectedValue}
                renderDropdownHeader={renderDropdownHeader}
                optionValue={(option) => option.value}
                placeholder={placeholder || ''}
                name={id}
                onBlur={handleBlur}
                onChange={handleChange}
                value={String(values[id] || '')}
                errorMsg={errors[id] && touched[id] ? errors[id] : undefined}
              />
            }
          />
        );
      case 'multiSelect':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            padding="0 0 6px"
            input={
              <MultiSelectChip
                name={id}
                placeholder={placeholder || ''}
                options={getMultiSelectOptions ? getMultiSelectOptions(values) : []}
                optionValue={(option) => option.value}
                disableOptionWhen={(option) => !!option.isDisabled}
                value={String(values[id] || '')
                  .split(',')
                  .filter(Boolean)}
                disabled={disabled ? disabled(values) : false}
                sort={false}
                onChange={(e) => {
                  setFieldValue(id, e.target.value);
                  const selectedIds = e.target.value.split(',').filter(Boolean);

                  setFieldValue(
                    'CreditAmount',
                    creditNotes
                      .filter((item) => selectedIds.includes(item.id))
                      .reduce((acc, item) => roundCurrency(acc + item.remainingCredit), 0),
                  );
                }}
                onDelete={(deleted) => {
                  const selectedIds = String(values[id] || '')
                    .split(',')
                    .filter(Boolean)
                    .filter((v) => v !== deleted);

                  setFieldValue(id, selectedIds.join(','));

                  setFieldValue(
                    'CreditAmount',
                    creditNotes
                      .filter((item) => selectedIds.includes(item.id))
                      .reduce((acc, item) => roundCurrency(acc + item.remainingCredit), 0),
                  );
                }}
                errorMsg={errors[id] && touched[id] ? errors[id] : undefined}
              />
            }
          />
        );

      case 'date':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            padding="0 0 6px"
            isBold={false}
            justifyContent="flex-start"
            input={
              <DatepickerSimple
                variant="outlineShadow"
                name={id}
                value={String(values[id] || '')}
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors[id] ? errors[id] : null}
                min={vp.createTimestamp(min)}
              />
            }
          />
        );
      case 'checkbox':
        return (
          <div className={styles.elinxToggle}>
            <Checkbox
              name={id}
              value={!values[id]}
              disabled={disabled ? disabled(values) : false}
              onBlur={() => {}}
              onChange={() => setFieldValue(id, !values[id])}
              size="sm"
              variant="rounded"
              hasNoPadding
            />

            <div className={styles.elinxToggleText}>
              <span className={styles.elinxToggleLabel}>{label}</span>
              {subtitle && <span className={styles.elinxToggleSubtitle}>{subtitle}</span>}
            </div>
          </div>
        );
      default:
        return <></>;
    }
  }

  function getValidationSchema(
    ctx: PaymentInformation,
    rows: PaymentInfoRows[],
    initialValidationSchema?: { [key: string]: any },
  ): { [key: string]: any } {
    const validations = rows.reduce(
      (existingFields, row) =>
        row.id && (row.container === 'field' || row.container === 'inputs')
          ? {
              ...existingFields,
              [row.id]: row.validate ? row.validate(ctx) : undefined,
            }
          : { ...existingFields, ...getValidationSchema(ctx, row.paymentRows) },
      { ...initialValidationSchema },
    );

    return validations;
  }
};

export default AccountingSystemSingleDialogue;
